import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Check, MessageCircle, Star, Heart, Ruler, Eye, Sparkles, Palette, MapPin, Clock } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import Media, { isVideo } from "../components/Media";
import LikeButton from "../components/LikeButton";

const Stars = ({ value, onChange, readOnly }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={readOnly}
        onClick={() => onChange && onChange(n)}
        data-testid={`star-${n}`}
      >
        <Star
          size={20}
          strokeWidth={1.5}
          className={n <= value ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#6B6A63]"}
        />
      </button>
    ))}
  </div>
);

const AngelDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [angel, setAngel] = useState(null);
  const [wall, setWall] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [fav, setFav] = useState(false);

  const [reviewRatings, setReviewRatings] = useState({ atencion: 5, higiene: 5, ambiente: 5, fidelidad: 5 });
  const [reviewText, setReviewText] = useState("");
  const [likeMap, setLikeMap] = useState({}); // target → {count, liked}

  const load = async () => {
    const a = await api.get(`/angels/${id}`);
    setAngel(a.data);
    const w = await api.get(`/angels/${id}/wall`);
    setWall(w.data);
    const r = await api.get(`/angels/${id}/reviews`);
    setReviews(r.data);
    api.post(`/angels/${id}/view`).catch(() => {});
    if (user) {
      try {
        const f = await api.get("/me/favorites");
        setFav(f.data.some((x) => x.angel_id === id));
      } catch (e) {}
    }
    // Hydrate likes for gallery + wall in one batch call
    const targets = [
      ...(a.data.gallery || []),
      ...(w.data.map((p) => p.image_path).filter(Boolean)),
    ];
    if (targets.length) {
      try {
        const lr = await api.post("/likes/batch", { targets });
        setLikeMap(lr.data || {});
      } catch (e) {}
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, user]);

  if (!angel) return <div className="min-h-screen"><Header /><div className="p-10 text-center">Cargando…</div></div>;

  const toggleFav = async () => {
    if (!user) { toast("Inicia sesión para guardar favoritas"); return; }
    if (fav) { await api.delete(`/me/favorites/${id}`); setFav(false); }
    else { await api.post(`/me/favorites/${id}`); setFav(true); toast.success("Guardada en favoritas"); }
  };

  const submitReview = async () => {
    if (!user) { toast("Inicia sesión para opinar"); return; }
    try {
      await api.post("/reviews", { angel_id: id, ratings: reviewRatings, comment: reviewText });
      toast.success("Gracias. Tu reseña será revisada antes de publicarse.");
      setReviewText("");
    } catch (e) { toast.error("No se pudo enviar"); }
  };

  const wa = angel.whatsapp ? `https://wa.me/${angel.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hola " + angel.artistic_name + ", te encontré en Secret Angels.")}` : null;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-[#F3F1EA] border border-[#E5E2D9]">
            {angel.main_photo_path && (
              <img
                src={fileUrl(angel.main_photo_path)}
                alt={angel.artistic_name}
                className="w-full h-full object-cover no-save"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            )}
            <div className="cherub-watermark absolute inset-0" />
            {angel.is_available && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/85 backdrop-blur px-3 py-1 rounded-full">
                <span className="w-2.5 h-2.5 available-dot rounded-full" />
                <span className="text-xs font-medium">Disponible ahora</span>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-4xl text-[#1A2118]">{angel.artistic_name}{angel.age ? `, ${angel.age}` : ""}</h1>
              {angel.verified && (
                <span className="inline-flex items-center gap-1 text-[#364531] bg-[#F3F1EA] border border-[#E5E2D9] px-2 py-0.5 rounded-full text-xs">
                  <Check size={14} /> Verificada
                </span>
              )}
            </div>
            <div className="text-sm text-[#6B6A63] mt-1">{angel.comuna}</div>

            <div className="flex gap-2 mt-4">
              {wa && (
                <a data-testid="whatsapp-btn" href={wa} target="_blank" rel="noreferrer"
                   onClick={() => { api.post(`/angels/${id}/click-wa`).catch(() => {}); }}
                   className="btn-primary inline-flex items-center gap-2">
                  <MessageCircle size={18} /> WhatsApp
                </a>
              )}
              <button data-testid="fav-detail-btn" onClick={toggleFav} className="btn-ghost inline-flex items-center gap-2">
                <Heart size={18} className={fav ? "fill-[#D4AF37] text-[#D4AF37]" : ""} />
                {fav ? "Favorita" : "Añadir favorita"}
              </button>
            </div>

            <div className="mt-6">
              <div className="text-xs tracking-[0.25em] uppercase text-[#6B6A63] mb-2">Atributos</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(angel.attributes || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 p-2 bg-[#F3F1EA]/60 rounded-lg">
                    <Sparkles size={14} className="text-[#364531]" />
                    <span className="text-[#6B6A63]">{k}:</span>
                    <span className="text-[#1A2118] font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-xs tracking-[0.25em] uppercase text-[#6B6A63] mb-2">Servicios</div>
              <div className="border border-[#E5E2D9] rounded-xl divide-y divide-[#E5E2D9] bg-white">
                {(angel.services || []).map((s, i) => (
                  <div key={i} className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm">{s.name}</span>
                    <span className="text-sm font-medium text-[#364531]">{s.price ? `$${s.price}` : "—"}</span>
                  </div>
                ))}
                {!(angel.services || []).length && (
                  <div className="px-4 py-3 text-sm text-[#6B6A63]">Sin servicios listados.</div>
                )}
              </div>
            </div>

            {(angel.additionals || []).length > 0 && (
              <div className="mt-6">
                <div className="text-xs tracking-[0.25em] uppercase text-[#6B6A63] mb-2">Adicionales</div>
                <div className="border border-[#E5E2D9] rounded-xl divide-y divide-[#E5E2D9] bg-white">
                  {(angel.additionals || []).map((s, i) => (
                    <div key={i} className="flex justify-between items-center px-4 py-3">
                      <span className="text-sm">{s.name}</span>
                      <span className="text-sm font-medium text-[#364531]">{s.price ? `$${s.price}` : "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {angel.bio && (
              <div className="mt-6 text-[#1A2118]/80 italic font-serif text-lg leading-relaxed">
                "{angel.bio}"
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="gallery" className="mt-10">
          <TabsList className="bg-[#F3F1EA]">
            <TabsTrigger data-testid="tab-gallery" value="gallery">Galería</TabsTrigger>
            <TabsTrigger data-testid="tab-wall" value="wall">Muro</TabsTrigger>
            <TabsTrigger data-testid="tab-reviews" value="reviews">Reseñas</TabsTrigger>
          </TabsList>

          <TabsContent value="gallery" className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(angel.gallery || []).map((p) => (
                <div key={p} className="relative aspect-[9/16] rounded-lg overflow-hidden bg-[#F3F1EA] no-save group">
                  <button
                    data-testid="gallery-thumb"
                    type="button"
                    onClick={() => setLightbox(p)}
                    className="block w-full h-full"
                  >
                    <Media path={p} watermark />
                  </button>
                  <div className="absolute bottom-2 right-2 z-10">
                    <LikeButton target={p} initial={likeMap[p]} />
                  </div>
                </div>
              ))}
            </div>
            {!(angel.gallery || []).length && <div className="text-sm text-[#6B6A63]">Galería vacía.</div>}

            {/* Map + Schedule below gallery */}
            <div className="grid md:grid-cols-2 gap-6 mt-10">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[#6B6A63] mb-2 flex items-center gap-2">
                  <MapPin size={14} /> Ubicación
                </div>
                <div className="border border-[#E5E2D9] rounded-xl overflow-hidden bg-[#F3F1EA] aspect-[4/3]">
                  {(angel.address || angel.comuna) ? (
                    <iframe
                      data-testid="location-map"
                      title="Ubicación"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${encodeURIComponent((angel.address || angel.comuna) + ", Chile")}&output=embed`}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-[#6B6A63]">Sin ubicación informada</div>
                  )}
                </div>
                {angel.address && <div className="text-xs text-[#6B6A63] mt-2">{angel.address}</div>}
              </div>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[#6B6A63] mb-2 flex items-center gap-2">
                  <Clock size={14} /> Horarios de atención
                </div>
                <div className="border border-[#E5E2D9] rounded-xl bg-white divide-y divide-[#E5E2D9]">
                  {(angel.schedule || []).map((s, i) => (
                    <div key={i} className="flex justify-between items-center px-4 py-3 text-sm">
                      <span className="text-[#1A2118] font-medium">{s.day}</span>
                      <span className="text-[#6B6A63]">{s.from} – {s.to}</span>
                    </div>
                  ))}
                  {!(angel.schedule || []).length && (
                    <div className="px-4 py-3 text-sm text-[#6B6A63]">Sin horario informado.</div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="wall" className="pt-6 space-y-4">
            {wall.map((p) => (
              <div key={p.post_id} className="border border-[#E5E2D9] rounded-xl bg-white p-4">
                <div className="text-xs text-[#6B6A63]">{new Date(p.created_at).toLocaleString("es-CL")}</div>
                {p.text && <div className="mt-2 text-[#1A2118] whitespace-pre-wrap">{p.text}</div>}
                {p.image_path && (
                  <div className="mt-3 relative rounded-lg overflow-hidden max-h-[28rem] bg-[#F3F1EA]">
                    {isVideo(p.image_path) ? (
                      <video src={fileUrl(p.image_path)} className="w-full max-h-[28rem] object-contain no-save"
                             controls muted playsInline loop controlsList="nodownload" disablePictureInPicture
                             onContextMenu={(e) => e.preventDefault()} />
                    ) : (
                      <img src={fileUrl(p.image_path)} alt="" className="w-full max-h-[28rem] object-cover no-save"
                           draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    )}
                    <div className="absolute bottom-2 right-2">
                      <LikeButton target={p.image_path} initial={likeMap[p.image_path]} />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {!wall.length && <div className="text-sm text-[#6B6A63]">Sin publicaciones aún.</div>}
          </TabsContent>

          <TabsContent value="reviews" className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.review_id} className="border border-[#E5E2D9] rounded-xl bg-white p-4">
                    <div className="flex justify-between text-xs text-[#6B6A63]">
                      <span>{r.user_name}</span>
                      <span>{new Date(r.created_at).toLocaleDateString("es-CL")}</span>
                    </div>
                    <div className="flex gap-3 mt-2 text-xs">
                      {Object.entries(r.ratings || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-1">
                          <span className="text-[#6B6A63]">{k}:</span>
                          <Stars value={v} readOnly />
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-sm text-[#1A2118]">{r.comment}</div>
                  </div>
                ))}
                {!reviews.length && <div className="text-sm text-[#6B6A63]">Sin reseñas aprobadas aún.</div>}
              </div>
              <div className="border border-[#E5E2D9] rounded-xl bg-white p-4 h-fit">
                <div className="font-serif text-xl mb-2">Deja tu reseña</div>
                {["atencion", "higiene", "ambiente", "fidelidad"].map((k) => (
                  <div key={k} className="flex justify-between items-center py-1">
                    <span className="capitalize text-sm text-[#6B6A63]">{k}</span>
                    <Stars value={reviewRatings[k]} onChange={(v) => setReviewRatings((s) => ({ ...s, [k]: v }))} />
                  </div>
                ))}
                <Textarea
                  data-testid="review-comment"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Tu experiencia (será moderada antes de publicarse)"
                  className="mt-2 bg-[#FAF9F6]"
                />
                <button data-testid="submit-review" onClick={submitReview} className="btn-primary mt-3">Enviar reseña</button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl bg-transparent border-none p-0">
          {lightbox && (
            isVideo(lightbox) ? (
              <video src={fileUrl(lightbox)} className="w-full max-h-[85vh] object-contain"
                     controls autoPlay muted playsInline loop controlsList="nodownload"
                     disablePictureInPicture onContextMenu={(e) => e.preventDefault()} />
            ) : (
              <img src={fileUrl(lightbox)} alt="" className="w-full max-h-[85vh] object-contain no-save"
                   draggable={false} onContextMenu={(e) => e.preventDefault()} />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AngelDetail;
